import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Users, RotateCcw, TableProperties, Sparkles, CheckCircle, UserPlus, Settings } from "lucide-react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AddGenericEventParticipantModal from "@/components/add-generic-event-participant-modal";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import GenericEventAttendedTable from "@/components/generic-event-attended-table";
import GenericEventStats from "@/components/generic-event-stats";
import RaffleModal from "@/components/raffle-modal";
import { PageBackground } from "@/components/ui/page-background";
import { TrackerHeader } from "@/components/tracker-header";
import type { JamNight, Performer } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { EventActionsDropdown } from "@/components/event-actions-dropdown";

interface GenericEventTrackerProps {
  jamNightId: number;
  activeTab?: string;
}

function GenericEventTrackerContent({ jamNightId, activeTab: urlActiveTab }: GenericEventTrackerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRaffleModal, setShowRaffleModal] = useState(false);
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState("signups");
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  // Tab change handler that syncs with URL
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setLocation(`/event/${jamNightId}/${value}`);
  };

  // Initialize tab from URL or use default
  useEffect(() => {
    if (urlActiveTab) {
      // Valid tabs for generic event tracker
      const validTabs = ["signups", "attendees", "manage"];
      if (validTabs.includes(urlActiveTab)) {
        setActiveTab(urlActiveTab);
      } else {
        // Invalid tab in URL, replace URL without creating history entry
        window.history.replaceState(null, '', `/event/${jamNightId}/signups`);
        setActiveTab("signups");
      }
    } else {
      // No tab in URL, replace URL without creating history entry
      window.history.replaceState(null, '', `/event/${jamNightId}/signups`);
      setActiveTab("signups");
    }
  }, [urlActiveTab, jamNightId]);

  const { data: jamNight, isLoading: jamNightLoading } = useQuery<JamNight>({
    queryKey: [`/api/events/${jamNightId}`],
  });

  const { data: performers = [], isLoading: performersLoading } = useQuery<
    Performer[]
  >({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
  });

  const resetAllMutation = useMutation({
    mutationFn: async () => {
      // Sort performers alphabetically by name
      const sortedPerformers = [...performers].sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      // Create reset promises with new sort orders
      const resetPromises = sortedPerformers.map((performer, index) =>
        apiRequest("PATCH", `/api/performers/${performer.id}`, {
          attended: false,
          sortOrder: (index + 1) * 1000, // Assign sort orders with gaps of 1000
        })
      );
      await Promise.all(resetPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "All participants have been reset successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reset participants. Please try again.",
        variant: "destructive",
      });
    },
  });

  const attendedParticipants = useMemo(() => 
    performers.filter((p) => p.attended && !p.removed), [performers]);
  const unattendedParticipants = useMemo(() => 
    performers.filter((p) => !p.attended && !p.removed), [performers]);

  const userRole = useMemo(() => {
    if (user?.role === 'god') return 'god';
    if (!user?.organisationUsers || !jamNight?.organisationId) return undefined;
    return user.organisationUsers.find(
      (ou) => ou.organisation.id === jamNight.organisationId
    )?.role;
  }, [user?.role, user?.organisationUsers, jamNight?.organisationId]);

  const isLoading = jamNightLoading || performersLoading;

  if (isLoading) {
    return (
      <PageBackground className="flex items-center justify-center">
        <div className="text-center relative z-10">
          <Users className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading event data...</p>
        </div>
      </PageBackground>
    );
  }

  const isEventCompleted = jamNight?.completed || false;

  return (
    <PageBackground>
      
      <TrackerHeader
        isEventCompleted={isEventCompleted}
        onAddParticipant={() => setShowAddModal(true)}
      />

      <main className="mx-auto px-4 sm:px-6 lg:px-32 xl:px-48 py-6 relative z-10">
        <h1 className="text-2xl font-bold text-slate-800 mb-6 drop-shadow-sm text-center">{jamNight?.name}</h1>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white/40 backdrop-blur-md mb-6">
            <TabsTrigger value="signups" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Signups
            </TabsTrigger>
            <TabsTrigger value="attendees" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Attendees
            </TabsTrigger>
            <TabsTrigger value="manage" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Manage
            </TabsTrigger>
          </TabsList>

          <TabsContent value="signups" className="space-y-6">
            <UnifiedParticipantsTable
              performers={unattendedParticipants}
              jamNightId={jamNightId}
              jamNight={jamNight}
              disabled={isEventCompleted}
              mode="generic-event"
            />
          </TabsContent>

          <TabsContent value="attendees" className="space-y-6">
            {attendedParticipants.length > 0 ? (
              <GenericEventAttendedTable
                performers={attendedParticipants}
                jamNightId={jamNightId}
                jamNight={jamNight}
                disabled={isEventCompleted}
              />
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <p className="text-slate-600">No attendees yet</p>
                <p className="text-sm text-slate-500 mt-2">
                  Mark participants as attended to see them here
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="manage" className="space-y-6">
            {/* Stats */}
            <GenericEventStats performers={performers.filter((p) => !p.removed)} />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                variant="outline"
                disabled={isEventCompleted || resetAllMutation.isPending || performers.length === 0}
                onClick={() => setShowResetAllConfirm(true)}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset All Participants
              </Button>

              <Button
                variant="outline"
                onClick={() => setLocation(`/event/${jamNightId}/participants`)}
              >
                <TableProperties className="h-4 w-4 mr-2" />
                View All Participant Data
              </Button>

              {jamNight?.raffleEnabled && (
                <Button
                  variant="outline"
                  className="bg-yellow-100/40 backdrop-blur-md border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 shadow-lg transition-all duration-300"
                  style={{
                    boxShadow: '0 4px 16px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                  }}
                  onClick={() => setShowRaffleModal(true)}
                  disabled={isEventCompleted || performers.filter(p => !p.removed && (p.raffleEntrances || 0) > 0).length === 0}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Run Raffle
                </Button>
              )}
            </div>

            {/* Event Management Dropdown */}
            {jamNight && (userRole === 'owner' || userRole === 'god') && (
              <div className="flex justify-center">
                <EventActionsDropdown
                  jamNight={jamNight}
                  canManageEvent={true}
                  variant="button"
                  className="w-full sm:w-auto"
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <ConfirmationDialog
          isOpen={showResetAllConfirm}
          onOpenChange={setShowResetAllConfirm}
          title="Reset All Participants?"
          description={"This will reset all participants back to \"not attended\" status. This action cannot be undone."}
          onConfirm={() => resetAllMutation.mutate()}
          confirmText="Yes, Reset All"
          isConfirming={resetAllMutation.isPending}
          confirmVariant="destructive"
        />
      </main>

      <AddGenericEventParticipantModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        jamNightId={jamNightId}
      />

      <RaffleModal
        open={showRaffleModal}
        onOpenChange={setShowRaffleModal}
        performers={performers.filter((p) => !p.removed)}
        jamNightId={jamNightId}
      />
    </PageBackground>
  );
}

export default function GenericEventTracker(props: GenericEventTrackerProps) {
  return <GenericEventTrackerContent {...props} />;
}